/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, { useEffect, useState, useContext } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import apiWrapper from '../../api';
import MainLayout from '../../components/main-layout';
import AssignmentsPage from './components/pages/assignments-page';
import NamePage from './components/pages/name-page';
import { authContext } from '../../contexts/auth-context';
import { isCourseActive as courseIsActive, nextBonusSelection, sortByLesson } from './utils';
import AssignmentSummaryPanel from './components/assignment-summary-panel';
import PersistenceWithBonusPage from './components/pages/persistence-with-bonus-page';
import AssignCoursesPage from './components/pages/assign-courses-page';
import ConfirmPage from './components/pages/confirm-page';
import './styles.css';

const AssignmentSetForm = ({ history, location }) => {
  const { auth } = useContext(authContext);

  const [syllabus, setSyllabus] = useState([]);
  const [currentLesson, setCurrentLesson] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [assignmentSetName, setAssignmentSetName] = useState();
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCoursesIds, setSelectedCoursesIds] = useState([]);
  const [selectedCoursesNames, setSelectedCoursesNames] = useState([]);
  const [assignmentSetId, setAssignmentSetId] = useState([]);
  const [bonusCourseIds, setBonusCourseIds] = useState([]);
  const [seededBonusCourseIds, setSeededBonusCourseIds] = useState([]);
  // currentPage starts with 1, currentPage where we start to show possible assignments starts with 2. Syllabus index starts with 0.
  const neededSylabusIndex = currentPage - 2;
  const assignPage = 12;
  const bonusPage = assignPage + 1;
  const [coursesAssignedToCurrentSet, setCoursesAssignedToCurrentSet] = useState([]);
  const [error, setError] = useState();
  const [assignmentSummaryLoaded, setAssignmentSummaryLoaded] = useState(false);
  const [byNavigate, setByNavigate] = useState(false);
  const [assignedCoursesSeeded, setAssignedCoursesSeeded] = useState(false);

  useEffect(() => {
    apiWrapper.getSyllabus().then((res) => {
      const lessons = res.data;
      lessons.sort((a, b) => a.display_order - b.display_order);
      setSyllabus(lessons);
    });
    apiWrapper.getCourses().then((res) => {
      setCourses(res.data);
    });
    // checking if we accessed this component clicking on edit button
    const fromEdit = location.state;
    if (fromEdit && fromEdit.chosenAssignmentSet) {
      const assignmentSetExist = fromEdit.chosenAssignmentSet;
      setAssignmentSetName(assignmentSetExist.name);
      setAssignmentSetId(assignmentSetExist.id);
    }
    if (location.state && location.state.page === assignPage) buildAssignmentSummary(fromEdit);
  }, [location.state]);

  // Tick the courses this set already owns as soon as the course list lands
  useEffect(() => {
    const isEdit = Boolean(location.state && location.state.chosenAssignmentSet);
    if (assignedCoursesSeeded || courses.length === 0) return;
    if (isEdit && typeof assignmentSetId !== 'number') return;
    getCoursesAssignedToCurrent();
    setAssignedCoursesSeeded(true);
  }, [courses, assignmentSetId, assignedCoursesSeeded, location.state]);

  useEffect(() => {
    if (currentPage > 1 && currentPage <= syllabus.length + 1) {
      window.scrollTo(0, 0);
      const lessonId = syllabus[neededSylabusIndex].id;
      apiWrapper.getAssignments(lessonId).then((res) => {
        setAssignments(res.data);
        // checking if we accessed this component clicking on edit button
        const fromEdit = location.state;
        if (fromEdit && fromEdit.chosenAssignmentSet) {
          const [selectedIds, selectedAverageTime] = getSelectedLessons(fromEdit.chosenAssignmentSet, neededSylabusIndex, res);
          setSelectedAssignments((prev) => {
            const lesson = syllabus[neededSylabusIndex];
            if (prev.some((assignment) => assignment.lesson.id === lesson.id)) return prev;
            return [...prev, { lesson, ids: selectedIds, averageTime: selectedAverageTime }];
          });
        }
      });
    }
  }, [currentPage, location.state, neededSylabusIndex, syllabus]);

  // Seed the bonus step from the courses picked on the assign step.
  // Editing an existing set starts from each course's current setting; a brand new set defaults everything on.
  useEffect(() => {
    if (currentPage !== bonusPage || courses.length === 0) return;
    const next = nextBonusSelection({
      courses,
      selectedCourseIds: selectedCoursesIds,
      bonusCourseIds,
      seededCourseIds: seededBonusCourseIds,
      isEdit: Boolean(location.state && location.state.chosenAssignmentSet),
    });
    if (next.bonusCourseIds.length !== bonusCourseIds.length
      || next.bonusCourseIds.some((id) => !bonusCourseIds.includes(id))) {
      setBonusCourseIds(next.bonusCourseIds);
    }
    if (next.changed) {
      setSeededBonusCourseIds(next.seededCourseIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, courses, selectedCoursesIds, location.state]);

  const buildAssignmentSummary = (fromEdit) => {
    setAssignmentSummaryLoaded(true);
    setCurrentPage(assignPage);
    getCoursesAssignedToCurrent();
    apiWrapper.getSyllabus().then(async (res) => {
      const lessons = res.data;
      lessons.sort((a, b) => a.display_order - b.display_order);
      let testAssignments = selectedAssignments;
      for (let page = 0; page < 10; page++) {
        const lessonId = lessons[page].id;
        const response = await apiWrapper.getAssignments(lessonId);
        // checking if we accessed this component clicking on edit button
        if (fromEdit && fromEdit.chosenAssignmentSet) {
          const [selectedIds, selectedAverageTime, alreadySelectedAssignments] = getSelectedLessons(fromEdit.chosenAssignmentSet, page, response);
          if (!alreadySelectedAssignments) {
            setSelectedAssignments([...testAssignments, {
              lesson: lessons[page],
              ids: selectedIds,
              averageTime: selectedAverageTime,
            }]);
            testAssignments = [...testAssignments, {
              lesson: lessons[page],
              ids: selectedIds,
              averageTime: selectedAverageTime,
            }];
          }
        }
      }
      setAssignmentSummaryLoaded(false);
    });
  };

  const getSelectedLessons = (chosenAssignmentSet, index, response) => {
    const assignmentSetExist = chosenAssignmentSet;
    // in assignments that we recieved from API, we are looking for assignments that have the same ids as assignments we recieved from location.state
    const selected = response.data.filter((element) => assignmentSetExist.assignments.some((item) => item.id === element.id));
    // transforming them in selectedAssignments form: {lesson: {}, ids: [], averageTime: number};
    const selectedIds = selected.map((item) => item.id);
    const selectedAverageTime = selected.reduce((acc, current) => acc + current.average_time, 0);
    // as this useEffect fires everytime currentPage changes, we should make sure that slectedAssignments that was set previously won't be modified
    const alreadySelectedAssignments = selectedAssignments.find((assignment) => assignment.lesson.id === syllabus[index].id);
    return [selectedIds, selectedAverageTime, alreadySelectedAssignments];
  };

  const getCoursesAssignedToCurrent = () => {
    const courseIds = selectedCoursesIds;
    const courseNames = selectedCoursesNames;
    const currentAssignmentSetCourses = coursesAssignedToCurrentSet;
    courses.forEach((course) => {
      const alreadyExists = selectedCoursesIds.find((id) => id === course.id);
      const addCurrentSetToCourse = coursesAssignedToCurrentSet.find((c) => c === course);
      if (course.assignment_set_id === assignmentSetId && !alreadyExists) {
        courseIds.push(course.id);
        courseNames.push(course.name);
        if (!addCurrentSetToCourse) currentAssignmentSetCourses.push(course);
      }
    });
    setSelectedCoursesIds([...courseIds]);
    setSelectedCoursesNames([...courseNames]);
    setCoursesAssignedToCurrentSet([...currentAssignmentSetCourses]);
  };

  const nameFormSubmit = (name) => {
    setAssignmentSetName(name);
    setCurrentPage(currentPage + 1);
  };

  const addAssignments = (ids, averageTime, lesson) => {
    const newAssignments = selectedAssignments.filter((assignment) => assignment.lesson.id !== lesson.id);
    setSelectedAssignments([...newAssignments, {
      lesson,
      ids,
      averageTime,
    }]);
  };

  const onNext = async (ids, averageTime, lesson, justSave) => {
    if (!justSave) {
      setCurrentLesson(syllabus[neededSylabusIndex + 1]);
      setCurrentPage(currentPage + 1);
    }
    await addAssignmentsToSummary();
    addAssignments(ids, averageTime, lesson);
  };

  const onBack = async (ids, averageTime, lesson) => {
    setCurrentLesson(syllabus[neededSylabusIndex - 1]);
    setCurrentPage(currentPage - 1);
    await addAssignmentsToSummary();
    if (currentPage < assignPage) addAssignments(ids, averageTime, lesson);
  };

  const onNavigate = (e) => {
    const page = parseInt(e.target.innerHTML);
    setCurrentLesson(syllabus[page - 2]);
    setCurrentPage(page);
    setByNavigate(true);
    addAssignmentsToSummary();
  };

  const addByNavigate = (ids, averageTime, lesson) => {
    addAssignments(ids, averageTime, lesson);
    addAssignmentsToSummary();
    setByNavigate(false);
  };

  const addAssignmentsToSummary = async () => {
    setAssignmentSummaryLoaded(true);
    const fromEdit = location.state && location.state.chosenAssignmentSet;
    for (const [index, lesson] of syllabus.entries()) {
      const alreadySelected = selectedAssignments.find((assignment) => assignment.lesson.id === lesson.id);
      if (!alreadySelected) {
        let entry = { lesson, ids: [], averageTime: 0 };
        if (fromEdit) {
          const response = await apiWrapper.getAssignments(lesson.id);
          const [selectedIds, selectedAverageTime] = getSelectedLessons(fromEdit, index, response);
          entry = { lesson, ids: selectedIds, averageTime: selectedAverageTime };
        }
        setSelectedAssignments((prev) => (
          prev.some((assignment) => assignment.lesson.id === lesson.id) ? prev : [...prev, entry]
        ));
      }
    }
    setAssignmentSummaryLoaded(false);
  };

  const assignCourses = async (assignmentSet) => {
    for (const course of courses) {
      if (selectedCoursesIds.indexOf(course.id) >= 0) {
        course.assignment_set = assignmentSet;
        course.assignment_set_id = assignmentSet.id;
        await apiWrapper.updateCourseAssignmentSet(course.id, assignmentSet.id);
      } else if (selectedCoursesIds.indexOf(course.id) < 0 && course.assignment_set_id === assignmentSet.id) {
        await apiWrapper.updateCourseAssignmentSet(course.id, 0);
      }
    }
  };

  const isCourseActive = (course) => courseIsActive(course, assignmentSetId);

  const activeCourses = courses.filter(isCourseActive);
  // Single source of truth for the bonus step - checkbox list, confirm summary and applyWithBonus
  // all read it, so nothing is written to without being shown. isCourseActive rather than the end
  // date alone keeps ended courses in this set reachable; with_bonus applies retroactively.
  const bonusEligibleCourses = activeCourses.filter((course) => selectedCoursesIds.includes(course.id));

  const applyWithBonus = async () => {
    for (const course of bonusEligibleCourses) {
      const withBonus = bonusCourseIds.includes(course.id);
      if (Boolean(course.with_bonus) !== withBonus) {
        await apiWrapper.updateCourseWithBonus(course.id, withBonus);
        course.with_bonus = withBonus;
      }
    }
  };

  const saveCourseAssignments = async (assignmentSet) => {
    await assignCourses(assignmentSet);
    await applyWithBonus();
  };

  const onConfirm = () => {
    const assignmentsId = sortByLesson(selectedAssignments).map((assignment) => assignment.ids).flat();
    const fromEdit = location.state;
    if (fromEdit && fromEdit.chosenAssignmentSet) {
      const assignmentSetExist = fromEdit.chosenAssignmentSet;
      if (assignmentSetExist.preset && auth.user.account_level !== 'admin') {
        saveCourseAssignments(fromEdit.chosenAssignmentSet).then(() => {
          history.replace('/assignment-sets');
        }).catch((err) => {
          setError(err.response.data.error ? err.response.data.error.message : 'Unknown error');
        });
        return;
      }
      apiWrapper.editAssignmentSet(assignmentSetExist.id, assignmentSetName, assignmentsId).then(() => {
        saveCourseAssignments(fromEdit.chosenAssignmentSet).then(() => {
          history.replace('/assignment-sets');
        }).catch((err) => {
          setError(err.response.data.error ? err.response.data.error.message : 'Unknown error');
        });
      }).catch((err) => {
        setError(err.response.data.error ? err.response.data.error.message : 'Unknown error');
      });
    } else {
      apiWrapper.createAssignmentSet(assignmentSetName, assignmentsId).then((res) => {
        saveCourseAssignments(res.data).then(() => {
          history.replace('/assignment-sets');
        }).catch((err) => {
          setError(err.response.data.error ? err.response.data.error.message : 'Unknown error');
        });
      }).catch((err) => {
        setError(err.response.data.error ? err.response.data.error.message : 'Unknown error');
      });
    }
  };

  const renderNavigation = () => {
    const renderCheckpoint = (name, index, key) => {
      const colorCheckpointStroke = (currentPage === index || currentPage > index) ? 'rgb(161, 206, 117)' : 'rgb(194, 196, 192)';
      const colorText = (currentPage === index || currentPage > index) ? 'rgb(161, 206, 117)' : 'grey';
      const checkpointBackground = currentPage > index ? 'rgb(161, 206, 117)' : 'white';
      const checkpointColorText = currentPage === index ? 'rgb(161, 206, 117)' : (currentPage > index ? 'white' : 'grey');

      return (
        <div key={key} style={{ position: 'relative' }}>
          <div className="d-flex">
            {index !== 1 && (
              <div className="stroke-container">
                <div className="stroke" style={{ backgroundColor: colorCheckpointStroke }} />
              </div>
            )}
            <div className="circle">
              <Button onClick={onNavigate} style={{ border: `2px solid ${colorCheckpointStroke}`, backgroundColor: checkpointBackground, color: checkpointColorText }} className="navigation" variant="success">
                {index}
              </Button>
              <p className="mt-2 checkpoint-title assignments-font-size" style={{ position: 'absolute', top: '100%', color: colorText }}>
                {index > 2 && index < assignPage ? `L${index - 2} ${name}` : name}
              </p>
            </div>
          </div>
        </div>
      );
    };
    return (
      <div className="d-flex wizard-nav">
        {renderCheckpoint('Name', 1)}
        {syllabus.map((lesson, index) => renderCheckpoint(lesson.short_name, index + 2, lesson.id))}
        {renderCheckpoint('Assign', assignPage)}
        {renderCheckpoint('Bonus', bonusPage)}
        {renderCheckpoint('Confirm', bonusPage + 1)}
      </div>
    );
  };

  const onCourseToggle = (chosenCourse) => {
    const alreadyExists = selectedCoursesIds.find((id) => id === chosenCourse.id);
    if (alreadyExists) {
      const newCoursesIds = selectedCoursesIds.filter((id) => id !== chosenCourse.id);
      const newCoursesNames = selectedCoursesNames.filter((name) => name !== chosenCourse.name);
      setSelectedCoursesIds(newCoursesIds);
      setSelectedCoursesNames(newCoursesNames);
    } else {
      setSelectedCoursesIds([...selectedCoursesIds, chosenCourse.id]);
      setSelectedCoursesNames([...selectedCoursesNames, chosenCourse.name]);
    }
  };

  const onBonusToggle = (chosenCourse) => {
    if (bonusCourseIds.includes(chosenCourse.id)) {
      setBonusCourseIds(bonusCourseIds.filter((id) => id !== chosenCourse.id));
    } else {
      setBonusCourseIds([...bonusCourseIds, chosenCourse.id]);
    }
  };

  const goToNextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  return (
    <MainLayout history={history}>
      <Row className="mt-4">
        <Col>
          <h1>Create a Custom Assignment Set</h1>
          <p className="assignments-font-size">Specify which assignments students are required to complete</p>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          {syllabus && (
            renderNavigation()
          )}
        </Col>
      </Row>
      {currentPage === 1 && (
        <NamePage onFormSubmitted={nameFormSubmit} defaultName={assignmentSetName} preset={location.state && location.state.chosenAssignmentSet ? location.state.chosenAssignmentSet.preset : false} />
      )}
      {assignments && currentPage > 1 && currentPage <= syllabus.length + 1 && (
        <Row className="mt-5">
          <Col sm={12} lg={7}>
            <AssignmentsPage
              assignments={assignments}
              lesson={currentLesson || syllabus[neededSylabusIndex]}
              index={neededSylabusIndex}
              onNext={onNext}
              assignmentsSelected={selectedAssignments}
              onBack={onBack}
              preset={location.state && location.state.chosenAssignmentSet ? location.state.chosenAssignmentSet.preset : false}
              byNavigate={byNavigate ? addByNavigate : null}
            />
          </Col>
          <Col sm={12} lg={5}>
            <AssignmentSummaryPanel assignments={selectedAssignments} loading={assignmentSummaryLoaded} />
          </Col>
        </Row>
      )}
      {currentPage === assignPage && (
        <AssignCoursesPage
          currentPage={currentPage}
          courses={activeCourses}
          selectedCourseIds={selectedCoursesIds}
          onToggle={onCourseToggle}
          onBack={onBack}
          onSave={goToNextPage}
          assignments={selectedAssignments}
          loading={assignmentSummaryLoaded}
        />
      )}
      {currentPage === bonusPage && (
        <PersistenceWithBonusPage
          currentPage={currentPage}
          courses={bonusEligibleCourses}
          bonusCourseIds={bonusCourseIds}
          onToggle={onBonusToggle}
          onBack={onBack}
          onSave={goToNextPage}
          assignments={selectedAssignments}
          loading={assignmentSummaryLoaded}
        />
      )}
      {currentPage > bonusPage && (
        <ConfirmPage
          currentPage={currentPage}
          assignmentSetName={assignmentSetName}
          assignmentSetId={assignmentSetId}
          assignments={selectedAssignments}
          error={error}
          courses={courses}
          selectedCourseIds={selectedCoursesIds}
          selectedCourseNames={selectedCoursesNames}
          bonusCourses={bonusEligibleCourses}
          bonusCourseIds={bonusCourseIds}
          onBack={onBack}
          onConfirm={onConfirm}
        />
      )}
    </MainLayout>
  );
};

export default AssignmentSetForm;
